'use client';

import { useEffect } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    const isChunkLoadError =
      error?.name === 'ChunkLoadError' ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('_next/static/chunks') ||
      error?.message?.includes('Failed to fetch');

    if (isChunkLoadError) {
      const lastReload = Number(sessionStorage.getItem('chunk_reload_attempt_at') || 0);
      const now = Date.now();

      if (!lastReload || now - lastReload > 30000) {
        sessionStorage.setItem('chunk_reload_attempt_at', String(now));
        const url = new URL(window.location.href);
        url.searchParams.set('_reload', String(now));
        window.location.replace(url.toString());
      }
    }
  }, [error]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-6 text-center">
      <div className="w-16 h-16 rounded-3xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-400 mb-4 shadow-xl">
        <AlertTriangle className="w-8 h-8" />
      </div>
      <h2 className="text-2xl font-extrabold text-white mb-2">Something went wrong</h2>
      <p className="text-xs text-slate-400 max-w-md mb-6 leading-relaxed">
        {error?.message || 'An unexpected error occurred while loading this page.'}
      </p>
      <div className="flex items-center gap-3">
        <button
          onClick={() => {
            sessionStorage.removeItem('chunk_reload_attempt_at');
            const url = new URL(window.location.href);
            url.searchParams.set('_reload', String(Date.now()));
            window.location.replace(url.toString());
          }}
          className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-indigo-600/25 flex items-center gap-2 transition-all"
        >
          <RefreshCw className="w-4 h-4" /> Reload Page
        </button>
        <button
          onClick={() => reset()}
          className="px-5 py-2.5 bg-slate-900 hover:bg-slate-800 border border-slate-800 text-slate-300 text-xs font-bold rounded-xl transition-all"
        >
          Try Again
        </button>
      </div>
    </div>
  );
}
